/** @jsxImportSource @emotion/react */
import { Header } from '@quickbit/backoffice-core-frontend';
import {
  Icon,
  SearchInput,
  Option,
  Table,
  Typography,
  useForm,
  useTheme,
  UtilityButton,
  UtilitySelect,
  DateInput,
  Pagination,
} from '@quickbit/qb-design-system';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAsyncFn, useDebounce, useSessionStorage } from 'react-use';
import { ExportCsv } from 'components/ExportCsv';
import { COUNTRY_OPTIONS } from 'utils';
import {
  AffiliateUserFilters,
  exportAffiliateUsers,
  exportHighRiskUsers,
  searchAffiliateUsers,
  SearchAffiliateUsersResponse,
} from '../api';
import { AccountStatus, getLabel } from '../types';

const PAGE_SIZE = 16;

const STATUS_OPTIONS = Object.keys(AccountStatus).map((key) => ({
  value: key,
  label: AccountStatus[key as keyof typeof AccountStatus],
}));

export const AffiliateUserList = () => {
  const { spacing, palette } = useTheme();
  const navigate = useNavigate();
  const headers = [
    { label: getLabel('uuid'), cellKey: 'uuid', sort: false },
    { label: getLabel('emailAddress'), cellKey: 'emailAddress', sort: false },
    {
      label: getLabel('externalReference'),
      cellKey: 'externalReference',
      sort: false,
    },
    { label: getLabel('riskScore'), cellKey: 'riskScore', sort: false },
  ];

  const [filterStore, setFilterStore] = useSessionStorage<{
    filters: AffiliateUserFilters;
    pagination: {
      page: number;
      size: number;
    };
  } | null>('qbAffiliateUserFilterStore');

  const [page, setPage] = useState(filterStore?.pagination.page || 1);

  const { fields, setters } = useForm<AffiliateUserFilters>(
    {
      userId: filterStore?.filters.userId,
      externalReference: filterStore?.filters.externalReference,
      country: filterStore?.filters.country,
      emailAddress: filterStore?.filters.emailAddress,
      firstName: filterStore?.filters.firstName,
      lastName: filterStore?.filters.lastName,
      personalIdNumber: filterStore?.filters.personalIdNumber,
      onboardingAtLte: filterStore?.filters.onboardingAtLte,
      onboardingAtGte: filterStore?.filters.onboardingAtGte,
      status: filterStore?.filters.status,
      riskScoreGte: filterStore?.filters.riskScoreGte,
    },
    {}
  );

  const [searchResult, setSearchStore] =
    useSessionStorage<SearchAffiliateUsersResponse>('affiliateSearchResult', {
      users: [],
      aggregatedData: { count: 0 },
    });

  const [{ loading }, search] = useAsyncFn(async () => {
    const appliedFilters = {
      filters: fields,
      pagination: {
        page: page,
        size: PAGE_SIZE,
      },
    };

    const data = await searchAffiliateUsers(appliedFilters);
    setFilterStore(appliedFilters);
    setSearchStore(data);
    return data;
  }, [page, fields]);

  const exportFunction = useCallback(() => {
    return exportAffiliateUsers({
      filters: fields,
    });
  }, [fields]);

  useDebounce(
    () => {
      void search();
    },
    500,
    [search]
  );
  const rows = useMemo(() => {
    return searchResult.users.map(
      ({ uuid, emailAddress, externalReference, riskScore }) => ({
        value: {
          uuid,
          emailAddress,
          externalReference,
          riskScore,
        },
      })
    );
  }, [searchResult]);

  const handleResetFilters = () => {
    setPage(1);
    setters.set({
      userId: undefined,
      externalReference: undefined,
      country: undefined,
      emailAddress: undefined,
      firstName: undefined,
      lastName: undefined,
      personalIdNumber: undefined,
      onboardingAtLte: null,
      onboardingAtGte: null,
      status: null,
      riskScoreGte: null,
    });
  };

  return (
    <>
      <Header>
        <div
          css={{
            display: 'grid',
            gridTemplateColumns: '1fr repeat(1, auto)',
            alignItems: 'center',
            gap: spacing.xxs,
          }}
        >
          <Typography variant="subtitle">Affiliate Users</Typography>
          <div css={{ display: 'flex', gap: spacing.xxs }}>
            <ExportCsv
              title="Export High Risk Users"
              csvFileName="high-risk-affiliate-users"
              exportFunction={exportHighRiskUsers}
              disabled={loading}
            />
            <ExportCsv
              csvFileName="affiliate-users"
              exportFunction={exportFunction}
              disabled={loading}
            />
          </div>
        </div>
      </Header>

      <div
        css={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
          gap: spacing.xxxs,
          alignItems: 'center',
          zIndex: 1,
        }}
      >
        <SearchInput
          placeholder="User ID"
          value={fields.userId ?? ''}
          onChange={(e) => {
            setPage(1);
            setters.userId?.set(e.target.value);
          }}
        />
        <SearchInput
          placeholder="Email"
          value={fields.emailAddress ?? ''}
          onChange={(e) => {
            setPage(1);
            setters.emailAddress?.set(e.target.value);
          }}
        />
        <SearchInput
          placeholder="External Reference"
          value={fields.externalReference ?? ''}
          onChange={(e) => {
            setPage(1);
            setters.externalReference?.set(e.target.value);
          }}
        />
        <SearchInput
          placeholder="First Name"
          value={fields.firstName ?? ''}
          onChange={(e) => {
            setPage(1);
            setters.firstName?.set(e.target.value);
          }}
        />
        <SearchInput
          placeholder="Last Name"
          value={fields.lastName ?? ''}
          onChange={(e) => {
            setPage(1);
            setters.lastName?.set(e.target.value);
          }}
        />
        <SearchInput
          placeholder="Personal ID Number"
          value={fields.personalIdNumber ?? ''}
          onChange={(e) => {
            setPage(1);
            setters.personalIdNumber?.set(e.target.value);
          }}
        />
        <SearchInput
          placeholder="Min Risk Score"
          value={fields.riskScoreGte?.toString() ?? ''}
          onChange={(e) => {
            setPage(1);
            setters.riskScoreGte?.set(
              e.target.value ? Number(e.target.value) : null
            );
          }}
        />
      </div>
      <div
        css={{
          marginTop: spacing.xxs,
          marginBottom: spacing.xxs,
          display: 'grid',
          gridTemplateColumns: 'repeat(99, auto) 1fr',
          alignItems: 'center',
          gap: spacing.xxxs,
          textWrap: 'nowrap',
        }}
      >
        <UtilitySelect
          label={
            fields.country
              ? COUNTRY_OPTIONS.find(
                  (countryOption) => countryOption.value === fields.country
                )?.label
              : 'Country'
          }
          listTitle="Country"
          options={COUNTRY_OPTIONS}
          value={
            fields.country
              ? COUNTRY_OPTIONS.find(
                  (countryOption) => countryOption.value === fields.country
                ) || null
              : null
          }
          onSelect={(data) => {
            setPage(1);
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            setters.country?.set((data as Option)?.value);
          }}
        />
        <DateInput
          placeholder="Onboarding all time"
          type="range"
          value={[
            fields.onboardingAtGte ? new Date(fields.onboardingAtGte) : null,
            fields.onboardingAtLte ? new Date(fields.onboardingAtLte) : null,
          ]}
          onChange={(e: [Date | null, Date | null]) => {
            setPage(1);
            setters.onboardingAtGte?.set(e[0]?.toISOString());
            setters.onboardingAtLte?.set(e[1]?.toISOString());
          }}
          withTime
        />
        <UtilitySelect
          label={
            fields.status
              ? STATUS_OPTIONS.find(
                  (statusOption) => statusOption.value === fields.status
                )?.label
              : 'Status'
          }
          listTitle="Status"
          options={STATUS_OPTIONS}
          value={
            fields.status
              ? STATUS_OPTIONS.find(
                  (statusOption) => statusOption.value === fields.status
                ) || null
              : null
          }
          onSelect={(data) => {
            setPage(1);
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            setters.status?.set((data as Option)?.value);
          }}
        />
        <UtilityButton
          variant="text"
          text="Reset filters"
          onClick={handleResetFilters}
        />
        <UtilityButton
          variant="secondary"
          text="Refresh"
          onClick={search}
          disabled={loading}
        />
        {loading && (
          <Icon
            name="spinner"
            className={{ svg: { color: palette.grey.default } }}
          />
        )}
      </div>
      <Table
        headers={headers}
        rows={rows}
        // eslint-disable-next-line @typescript-eslint/no-base-to-string, @typescript-eslint/restrict-template-expressions
        onRowClick={(row) => navigate(`${row.uuid}`)}
      />
      <div css={{ marginTop: spacing.xxs }}>
        <Pagination
          currentPage={page}
          onChange={setPage}
          numberOfElements={searchResult.aggregatedData.count}
          pageSize={PAGE_SIZE}
        />
      </div>
    </>
  );
};
